import { ActionIcon, Container, Flex, Menu, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconDots, IconEdit, IconPlus, IconScissors, IconSwitchHorizontal, IconTrash } from '@tabler/icons-react'
import { useParams } from '@tanstack/react-router'
import { Row } from '@tanstack/table-core'
import { useDeleteVariant } from 'hooks/variants'
import { MRT_PaginationState, MRT_TableOptions, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useEffect, useState } from 'react'
import {
  Category,
  FabricVariant,
  PillowVariant,
  PremadeVariant,
  TableVariant,
  TowelVariant,
  Unit,
  Variant,
} from 'types/products'
import { getCategoryName } from 'utils'

interface Props<T extends Variant> extends MRT_TableOptions<T> {
  data: Array<T>
  highlightItemId?: string
  onRowClick?: (variant: T) => void
}

export function useProductTable<T extends Variant>({ data, highlightItemId, onRowClick, ...props }: Props<T>) {
  const params: { productId: string; section: Category } | undefined = useParams({
    from: '/_auth/product/$productId/{-$section}',
    select: (p) => p,
    shouldThrow: false,
  })
  const productId = params?.productId
  const category = params?.section as Category
  const [tableData, setTableData] = useState(data)
  const [timedHighlightItemId, setTimedHighlightItemId] = useState<string | undefined>(highlightItemId)
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 5 })
  const [isFullScreen, setIsFullScreen] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimedHighlightItemId(undefined)
    }, 7000)

    return () => clearTimeout(timer) // Cleanup on unmount or when highlightItemId changes
  }, [highlightItemId])

  useEffect(() => {
    // make the highlighted item first in the list
    if (highlightItemId) {
      const index = data.findIndex((item) => item.id === highlightItemId)
      if (index > -1) {
        const item = data[index]
        const newData = [item, ...data.slice(0, index), ...data.slice(index + 1)]
        setTableData(newData)
      } else {
        setTableData(data)
      }
    } else {
      setTableData(data)
    }
  }, [data])

  const deleteVariant = useDeleteVariant()
  function openDeleteModal(variantId: string) {
    const variant = tableData.find((v) => v.id === variantId)
    if (!variant) return
    modals.openConfirmModal({
      title: 'Usuń produkt',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć <b>{variant.stock}</b>{' '}
          {variant.unit === Unit.METER ? 'metrów' : variant.unit === Unit.CENTIMETER ? 'centymetrów' : 'sztuk'} produktu
          o {variant.unit === Unit.PIECE ? 'wymiarze' : 'szerokości'} <b>{variant.size}</b>{' '}
          {variant.unit === Unit.PIECE ? '' : 'cm'} z półki{' '}
          <b>
            {variant.place.rack}-{variant.place.column}-{variant.place.shelf}
          </b>
          ? Nie będzie można cofnąć tej operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń produkt', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteVariant.mutateAsync(variant),
    })
  }

  function openTakeModal(variantId: string) {
    const variant = tableData.find((v) => v.id === variantId)
    if (!variant) return
    modals.openContextModal({
      modal: 'takeProduct',
      centered: true,
      title: 'Pobierz produkt z magazynu',
      innerProps: {
        variant: variant,
      },
    })
  }

  function openMovePlaceModal(variantId: string) {
    const variant = tableData.find((v) => v.id === variantId)
    if (!variant) return
    modals.openContextModal({
      modal: 'movePlace',
      centered: true,
      title: 'Przenieś produkt na inną półkę',
      innerProps: {
        variant: variant,
      },
    })
  }

  function openEditVariantModal(variantId: string) {
    const variant = tableData.find((v) => v.id === variantId)
    if (!variant) return

    const properties = {
      modal: 'editVariant',
      centered: true,
      title: 'Edytuj produkt',
    } as const

    if (variant.category === Category.FABRIC) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as FabricVariant,
          category: Category.FABRIC,
        },
      })
    } else if (variant.category === Category.PREMADE) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as PremadeVariant,
          category: Category.PREMADE,
        },
      })
    } else if (variant.category === Category.PILLOW) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as PillowVariant,
          category: Category.PILLOW,
        },
      })
    } else if (variant.category === Category.TOWEL) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as TowelVariant,
          category: Category.TOWEL,
        },
      })
    } else if (variant.category === Category.TABLE) {
      modals.openContextModal({
        ...properties,
        innerProps: {
          variant: variant as TableVariant,
          category: Category.TABLE,
        },
      })
    }
  }

  function openAddVariantModal() {
    if (!productId || !category) return
    modals.openContextModal({
      modal: 'addVariant',
      centered: true,
      title: `Dodaj ${getCategoryName(category).biernik}`,
      innerProps: {
        productId,
        category,
      },
    })
  }

  return useMantineReactTable({
    ...props,
    autoResetPageIndex: false,
    data: tableData,
    layoutMode: 'grid',
    enableColumnActions: false,
    enableColumnFilters: true,
    enableTopToolbar: true,
    enableHiding: false,
    enableDensityToggle: false,
    enableGlobalFilter: false,
    enableBottomToolbar: true,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      showRowsPerPage: true,
      rowsPerPageOptions: ['5', '10', '15', '20'],
    },
    onIsFullScreenChange: () => {
      const newIsFullScreen = !isFullScreen
      setIsFullScreen(newIsFullScreen)
      if (newIsFullScreen) {
        setPagination({ pageIndex: 0, pageSize: 10 })
      } else {
        setPagination({ pageIndex: 0, pageSize: 5 })
      }
    },
    initialState: {
      showColumnFilters: true,
    },
    state: {
      pagination,
      isFullScreen,
    },
    onPaginationChange: setPagination,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
      },
    },
    mantineTableBodyCellProps: ({ row, cell }) => {
      if (!cell.id.endsWith('mrt-row-actions')) {
        return {
          style: {
            cursor: onRowClick ? 'pointer' : undefined,
          },
          onClick: () => onRowClick?.(row.original),
        }
      } else {
        return {}
      }
    },
    mantineTableBodyRowProps: ({ row }) => {
      return {
        style: {
          backgroundColor: row.original.id === timedHighlightItemId ? 'var(--mantine-color-brand-light)' : undefined,
          fontWeight: row.original.id === timedHighlightItemId ? '600' : undefined,
        },
      }
    },
    renderTopToolbarCustomActions: () => {
      if (!productId || !category) return null
      return (
        <Container mr={0} pr={0}>
          <Tooltip label="Dodaj produkt">
            <ActionIcon size="lg" variant="subtle" onClick={() => openAddVariantModal()} ml="auto">
              <IconPlus />
            </ActionIcon>
          </Tooltip>
        </Container>
      )
    },
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Pobierz">
          <ActionIcon radius="xl" color="brand" onClick={() => openTakeModal(row.original.id)}>
            <IconScissors size={18} />
          </ActionIcon>
        </Tooltip>
        <Menu withArrow arrowSize={10} position="bottom-end" withinPortal offset={0} arrowOffset={8}>
          <Menu.Target>
            <ActionIcon radius="xl" variant="subtle" color="gray">
              <IconDots size={18} stroke={2} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconSwitchHorizontal size={16} stroke={1.5} />}
              onClick={() => openMovePlaceModal(row.original.id)}
            >
              Przenieś
            </Menu.Item>
            <Menu.Item
              leftSection={<IconEdit size={16} stroke={1.5} />}
              onClick={() => openEditVariantModal(row.original.id)}
            >
              Edytuj
            </Menu.Item>
            <Menu.Item
              leftSection={<IconTrash size={16} stroke={1.5} />}
              color="red"
              onClick={() => openDeleteModal(row.original.id)}
            >
              Usuń
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
    ),
  })
}

export function sizeSortingFn<TData>(rowA: Row<TData>, rowB: Row<TData>, columnId: string): number {
  const a = rowA.getValue<string>(columnId)
  const b = rowB.getValue<string>(columnId)

  const [aW, aH = '0'] = a.split('x')
  const [bW, bH = '0'] = b.split('x')

  const widthDiff = parseInt(aW, 10) - parseInt(bW, 10)
  if (widthDiff !== 0) {
    return widthDiff
  }

  return parseInt(aH, 10) - parseInt(bH, 10)
}
